/**
 * The inside of an edit-bookmark window: two columns, a footer, and one state at a time.
 *
 * Everything that decides anything is in
 * [se.soderbjorn.lunapin.client.viewmodel.EditBookmarkBackingViewModel]. This file paints
 * one of its states and reports gestures back, exactly as `BookmarkListView`
 * does for the list — so an Android or iOS editor renders the same states from the same
 * flow rather than rediscovering what a 404 on a save means.
 *
 * ── The split, which is the design's whole argument ──────────────────────────
 *
 * **Left is yours to type in**: title, URL, tags, note. **Right is what the system
 * knows**: the AI summary and the details — when it was added, where it is in the queue.
 * On a darker ground, with a divider between.
 *
 * The reason is not decoration. SheetMark mixed the two into one scrolling stack and it
 * was never clear what a Save would overwrite. Here the boundary answers it: *the left
 * column is what Save writes, the right column is what it does not.* The URL sits on the
 * left because it belongs to the bookmark rather than to the system, and it is drawn
 * recessed — a fact rather than a field — because this window will not change it. That is
 * a UI decision and not a server limit: `PUT /api/bookmarks/{id}` can move a bookmark to a
 * new URL, and the view model's header argues why a text box holding one is a bad trade.
 *
 * ── Why this one is not rebuilt wholesale on every state ─────────────────────
 *
 * Every other surface in `:web` repaints from scratch, because there is nothing in any of
 * them to preserve. There is here: a caret, a selection, and a half-typed `#tag` with a
 * menu open under it. So the frame is built once and [EditBookmarkView.render] updates the
 * parts that changed, writing into an input only when its value genuinely differs from the
 * state — which is what makes the initial fill work without fighting the person typing.
 *
 * The three panels — loading, the form, gone — are swapped rather than rebuilt for the
 * same reason: a form that is torn down and rebuilt when a save comes back loses the field
 * somebody was about to correct.
 *
 * ── What the design canvas draws that is deliberately not here ───────────────
 *
 *  - **The dashed `AI SUGGESTED` chip row** under the tag field. There is nothing to
 *    suggest: the model's tags are already on the bookmark by the time this window opens.
 *    They read differently in the field — one small `AI` mark on the chip — and that is
 *    the whole treatment.
 *  - **A `Tab:` row.** Tabs are arrangement, not filing. Nothing ties a bookmark to one.
 *  - **A `Source:` row.** There is no such field and there is not going to be.
 *  - **`EDITED 2 MIN AGO`** in the header. Nothing tracks it, and adding a column to make
 *    a mockup true is the wrong direction of travel.
 *
 * @see se.soderbjorn.lunapin.client.viewmodel.EditBookmarkBackingViewModel
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.KeyboardEvent
import kotlin.js.Date
import se.soderbjorn.lunapin.client.viewmodel.EditBookmarkBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.EditBookmarkBackingViewModel.State
import se.soderbjorn.lunapin.clientserver.AiStates

/**
 * One edit window's DOM, and the small amount of state that belongs to a *view* rather
 * than to the model: which panel is showing, and what is in the two text boxes.
 *
 * @param model the window's backing view model. Every gesture goes to it and nothing is
 *   decided here.
 * @param onClose what Cancel, `Esc` and a completed delete ask for. The view cannot close
 *   a window — that is the workspace's business — so it asks.
 * @param onTitleChanged the label this window should now wear in the sidebar and its own
 *   chrome, or null while there is nothing better than a placeholder. Reported rather than
 *   read, because the shell asks for a pane's label synchronously and has no way to wait
 *   for a fetch.
 */
internal class EditBookmarkView(
    private val model: EditBookmarkBackingViewModel,
    private val onClose: () -> Unit,
    private val onTitleChanged: (String?) -> Unit,
) {
    /** What the toolkit mounts. */
    val root: HTMLElement = element("div", "lp-edit")

    private val loadingPanel = element("div", "lp-edit-loading", "Loading…")
    private val gonePanel = element("div", "lp-edit-gone")
    private val failedPanel = element("div", "lp-edit-failed")
    private val form = element("div", "lp-edit-form")

    private val titleField = (document.createElement("input") as HTMLInputElement).apply {
        type = "text"
        className = "lp-edit-title-input"
        placeholder = "Untitled"
        // The title is the one field a person is most likely to arrive wanting to fix, and
        // it is also what names the window — so it is the largest type on the surface.
        addEventListener("input", { model.onTitleEdited(value) })
    }

    private val urlText = element("span", "lp-edit-url-text")
    private val copyButton = button("Copy", "lp-edit-url-copy") { copyUrl() }

    /**
     * The shared `#tag` control from LPN-14, not a second implementation of it.
     *
     * `allowExclude = false` because a *not this one* is a filter and this field files;
     * `offerCreate = true` because this is a field that puts a tag on a bookmark, which is
     * the only kind that may invent one.
     */
    private val tagField = TagInputField(
        placeholder = "#tag",
        allowExclude = false,
        offerCreate = true,
        onCommit = { model.onTagAdded(it.tag) },
        onRemove = { model.onTagRemoved(it.tag) },
    )

    private val noteField = (document.createElement("textarea") as HTMLTextAreaElement).apply {
        className = "lp-edit-note-input"
        placeholder = "Your own note. Never what the model wrote."
        addEventListener("input", { model.onNoteEdited(value) })
    }

    /**
     * *Regenerate* — read the page again and write a new summary.
     *
     * Built once rather than rebuilt per state, unlike the footer's buttons: this one is
     * inside the right column's header, which is not repainted, and its two states are a
     * label and a `disabled` rather than a different set of controls.
     */
    private val regenerateButton = button("Regenerate", "lp-edit-regenerate") {
        model.onRegenerateRequested()
    }

    private val aiSummaryText = element("div", "lp-edit-ai-summary")
    private val aiStateValue = element("span", "lp-edit-detail-value")
    private val addedValue = element("span", "lp-edit-detail-value")

    private val message = element("div", "lp-edit-message")
    private val footerActions = element("div", "lp-edit-footer-actions")

    /** The URL last painted, so the copy button always copies what is on screen. */
    private var currentUrl: String = ""

    /** The label last reported, so an unchanged one does not ask the shell to repaint. */
    private var reportedTitle: String? = null

    init {
        root.appendChild(loadingPanel)
        root.appendChild(buildForm())
        root.appendChild(gonePanel)
        root.appendChild(failedPanel)
        // `⌘⏎` and `Esc`, on the window's own root rather than on the document: two editors
        // can be open at once, and a document-level listener would have to work out which
        // one the keystroke was meant for. Here the answer is where the focus is.
        root.addEventListener("keydown", { event -> onKeyDown(event as KeyboardEvent) })
    }

    /**
     * Paint one state.
     *
     * @param state the whole of what to show. Exactly one of its fields is non-null, and
     *   this function's shape is that fact: four panels, one shown.
     */
    fun render(state: State) {
        loadingPanel.hidden = state.loading == null
        form.hidden = state.ready == null
        gonePanel.hidden = state.gone == null
        failedPanel.hidden = state.failed == null

        state.failed?.let { failedPanel.textContent = it.message }
        state.gone?.let { renderGone() }
        state.ready?.let { renderForm(it) }
        report(state)
    }

    // ── The form ─────────────────────────────────────────────────────────────

    private fun buildForm(): HTMLElement {
        val columns = element("div", "lp-edit-columns")
        columns.appendChild(buildLeftColumn())
        columns.appendChild(buildRightColumn())
        form.appendChild(columns)
        form.appendChild(buildFooter())
        return form
    }

    /** What a Save overwrites. */
    private fun buildLeftColumn(): HTMLElement {
        val column = element("div", "lp-edit-left")
        column.appendChild(labelled("Title", titleField))

        // Recessed, ellipsised on one line, with a copy glyph at its right edge: it reads
        // as a fact rather than as a field, which is what it is here.
        val url = element("div", "lp-edit-url")
        url.appendChild(urlText)
        url.appendChild(copyButton)
        column.appendChild(labelled("URL", url))

        column.appendChild(labelled("Tags", tagField.root))
        column.appendChild(labelled("Note", noteField, "lp-edit-field lp-edit-field-grow"))
        return column
    }

    /** What a Save does not touch. */
    private fun buildRightColumn(): HTMLElement {
        val column = element("div", "lp-edit-right")

        val header = element("div", "lp-edit-ai-header")
        header.appendChild(element("span", "lp-edit-ai-pill", "AI"))
        header.appendChild(element("span", "lp-edit-label", "Summary"))
        header.appendChild(element("span", "lp-edit-spacer"))
        // In the right column's own header rather than in the footer beside Save, and that
        // is the whole argument of the two-column split applied to a button: the footer
        // writes the left column, and this does not write anything the user typed. Putting
        // it next to Save would make it look like part of the same act.
        header.appendChild(regenerateButton)
        column.appendChild(header)
        column.appendChild(aiSummaryText)

        column.appendChild(element("div", "lp-edit-label lp-edit-details-label", "Details"))
        val details = element("div", "lp-edit-details")
        details.appendChild(detailRow("Added", addedValue))
        // Not in the canvas's list, and worth one row: `none`, `pending` and `failed` are
        // the three answers to "why is there no summary here", and a reader looking at an
        // empty panel has no other way to tell them apart.
        details.appendChild(detailRow("AI", aiStateValue))
        column.appendChild(details)
        return column
    }

    private fun buildFooter(): HTMLElement {
        val footer = element("div", "lp-edit-footer")
        footer.appendChild(message)
        footer.appendChild(footerActions)
        return footer
    }

    private fun renderForm(ready: State.Ready) {
        // Written only when it differs, so a repaint mid-typing cannot move the caret to
        // the end of what somebody is halfway through.
        if (titleField.value != ready.title) titleField.value = ready.title
        if (noteField.value != ready.note) noteField.value = ready.note
        titleField.disabled = ready.isSaving
        noteField.disabled = ready.isSaving

        currentUrl = ready.url
        urlText.textContent = ready.url
        urlText.title = ready.url

        tagField.setKnownTags(ready.knownTags)
        // The `AI` mark comes from the bookmark's own `aiTags`, which is a subset of its
        // tags rather than a second list: a chip says who put it there and is otherwise an
        // ordinary chip.
        val aiTags = ready.bookmark.aiTags.toSet()
        tagField.setCommitted(ready.tags.map { CommittedTag(tag = it, fromAi = it in aiTags) })

        aiSummaryText.textContent = ready.bookmark.aiSummary.ifBlank { aiPlaceholder(ready) }
        aiSummaryText.className =
            if (ready.bookmark.aiSummary.isBlank()) "lp-edit-ai-summary lp-edit-ai-empty"
            else "lp-edit-ai-summary"

        // Disabled while one is already coming, because pressing it then would only reset
        // this bookmark's attempt count and its place in the queue. The label says which of
        // the two it is, so the disabled state is explained rather than merely enforced.
        regenerateButton.textContent = if (ready.isAwaitingSummary) "Summarising…" else "Regenerate"
        regenerateButton.disabled = ready.isAwaitingSummary

        addedValue.textContent = formatInstant(ready.bookmark.createdAt)
        // The state, and — when it failed — the server's own sentence about why, on hover.
        // That sentence has nowhere else to be on this window: the summary panel says a
        // summary was tried, and *why* is a detail somebody asks for rather than reads.
        aiStateValue.textContent = ready.bookmark.aiState
        aiStateValue.title = ready.bookmark.aiError

        message.textContent = ready.message ?: ""
        message.hidden = ready.message == null

        renderFooterActions(ready)
    }

    /**
     * The footer's buttons, rebuilt per state.
     *
     * The one part of this surface that *is* repainted wholesale, and it can be: there is
     * nothing in a row of buttons to preserve, and the confirmation swaps three controls
     * for two. Building it by hand each time is cheaper than hiding and unhiding five.
     */
    private fun renderFooterActions(ready: State.Ready) {
        footerActions.innerHTML = ""
        if (ready.isConfirmingDelete) {
            footerActions.appendChild(
                element("span", "lp-edit-confirm-text", "Delete this bookmark?"),
            )
            footerActions.appendChild(
                button("Delete", "lp-edit-danger") { model.onDeleteConfirmed() },
            )
            footerActions.appendChild(
                button("Keep it", "lp-edit-secondary") { model.onDeleteCancelled() },
            )
            return
        }
        // Outlined rather than filled: destructive, and reached on purpose rather than by
        // the hand that is already moving towards Save.
        footerActions.appendChild(
            button("Delete", "lp-edit-danger") { model.onDeleteRequested() },
        )
        footerActions.appendChild(element("span", "lp-edit-spacer"))
        footerActions.appendChild(
            element("span", "lp-edit-hint", "⌘⏎ save · esc close"),
        )
        footerActions.appendChild(button("Cancel", "lp-edit-secondary") { onClose() })
        val save = button(if (ready.isSaving) "Saving…" else "Save", "lp-edit-primary") {
            model.onSaveRequested()
        }
        save.disabled = ready.isSaving
        footerActions.appendChild(save)
    }

    /**
     * Why the summary panel is empty, in the words the state actually justifies.
     *
     * Four answers rather than one blank, because "no summary" is four different facts and
     * only one of them is worth doing anything about. The failed one names *Regenerate*
     * rather than offering a second button beside it — the control is on screen, a few
     * centimetres above, and a panel with its own copy of it would be two ways to press one
     * thing.
     */
    private fun aiPlaceholder(ready: State.Ready): String = when (ready.bookmark.aiState) {
        AiStates.PENDING, AiStates.RUNNING -> "Waiting for a summary…"
        AiStates.FAILED ->
            "A summary was tried and did not arrive. " +
                ready.bookmark.aiError.ifBlank { "Regenerate to try again." }

        else -> "No summary. Nothing has been asked for."
    }

    // ── The state with no form in it ─────────────────────────────────────────

    /**
     * *This bookmark is gone.*
     *
     * A real panel rather than an empty window, because the alternative is somebody
     * pressing Save on a form for a row that no longer exists — which used to mean
     * recreating it. The window stays open and says so; closing it is the user's move.
     */
    private fun renderGone() {
        if (gonePanel.childElementCount > 0) return
        gonePanel.appendChild(
            element("div", "lp-edit-gone-heading", "This bookmark is gone."),
        )
        gonePanel.appendChild(
            element(
                "div",
                "lp-edit-gone-text",
                "It was deleted — here, in another window, or from your phone. There is " +
                    "nothing left to edit, and saving would only make a new one.",
            ),
        )
        val actions = element("div", "lp-edit-gone-actions")
        actions.appendChild(button("Close window", "lp-edit-primary") { onClose() })
        // Reachable rather than a dead end: this state is also what a wrong answer looks
        // like, and asking again costs one request.
        actions.appendChild(
            button("Look again", "lp-edit-secondary") { model.onReloadRequested() },
        )
        gonePanel.appendChild(actions)
    }

    // ── The window's own name ────────────────────────────────────────────────

    /**
     * Tell the shell what this window is called now.
     *
     * The bookmark's own title, falling back to its URL when there is none and to nothing
     * at all before it has loaded — which is what makes two open editors tellable apart in
     * the sidebar. Reported only when it changes, because the shell's answer to being told
     * is to re-render itself.
     *
     * A window whose bookmark is gone keeps its last name rather than becoming "Bookmark"
     * again: the sidebar row is how somebody finds the window to close, and renaming it out
     * from under them at the moment it needs attention is the wrong direction.
     */
    private fun report(state: State) {
        val title = state.ready?.let { it.title.ifBlank { it.url } } ?: return
        if (title == reportedTitle) return
        reportedTitle = title
        onTitleChanged(title)
    }

    // ── Gestures ─────────────────────────────────────────────────────────────

    private fun onKeyDown(event: KeyboardEvent) {
        // Nothing here checks `defaultPrevented`, and that is worth a line because it was
        // tried: the toolkit marks Escape handled at a level above this window, so the flag
        // is true for *every* Escape and a window that consulted it would never close.
        // What keeps Escape from doing two things at once is the other end — the tag field
        // stops the keys it consumes (see `TagInputField.consume`), so a keystroke that
        // dismissed a menu never arrives here at all.
        //
        // `metaKey` or `ctrlKey`: the design says ⌘⏎ and this is a browser, where the same
        // gesture is Ctrl on everything that is not a Mac.
        if (event.key == "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            model.onSaveRequested()
            return
        }
        if (event.key == "Escape") {
            event.preventDefault()
            onClose()
        }
    }

    /**
     * Put the URL on the clipboard.
     *
     * `navigator.clipboard` and no fallback. The old one — a hidden `<textarea>` and
     * `document.execCommand("copy")` — is deprecated everywhere, and the modern API is
     * available on every browser that can run this bundle. Reached dynamically because
     * Kotlin's `Navigator` external predates it; a failure is swallowed, since it means the
     * page does not have permission and there is nothing useful to say about that in a
     * window about a bookmark.
     */
    private fun copyUrl() {
        val url = currentUrl.ifBlank { return }
        runCatching { window.navigator.asDynamic().clipboard.writeText(url) }
        copyButton.textContent = "Copied"
        window.setTimeout({ copyButton.textContent = "Copy" }, COPIED_FOR_MS)
    }

    // ── Small builders ───────────────────────────────────────────────────────

    /** A field: a small mono uppercase label above its control. */
    private fun labelled(
        label: String,
        control: HTMLElement,
        className: String = "lp-edit-field",
    ): HTMLElement {
        val field = element("div", className)
        field.appendChild(element("div", "lp-edit-label", label))
        field.appendChild(control)
        return field
    }

    /** A details row: label left, value right in a mono face. */
    private fun detailRow(label: String, value: HTMLElement): HTMLElement {
        val row = element("div", "lp-edit-detail")
        row.appendChild(element("span", "lp-edit-detail-label", label))
        row.appendChild(value)
        return row
    }

    private companion object {
        /** How long the copy button says it worked. Long enough to read, short enough not to lie. */
        const val COPIED_FOR_MS = 1400
    }
}

/**
 * An epoch-millis instant, in the reader's own locale and time zone.
 *
 * `Date` rather than kotlinx-datetime: the browser already knows what the user's zone is
 * and how they write a date, and a formatting library in the bundle would be a second
 * opinion about both. Not shared with Android or iOS for the same reason — each platform's
 * own formatter is the right answer there too.
 */
private fun formatInstant(millis: Long): String =
    Date(millis.toDouble()).toLocaleString()
